import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { BlockBlobClient } from '@azure/storage-blob';
import { CreateUploadRequestDTO, CreateUploadResponseDTO } from '../models/chatbot-file-dto';

type UploadCheckpoint = {
  blobname: string;
  fileName: string;
  fileSize: number;
  blockSize: number;
  uploaded: number[];          // índices de bloque subidos
  createdAt: number;
};

@Injectable({
  providedIn: 'root'
})
export class FileService {

  private pathAPI = environment.urlAPI;
  private controller = "File/";

  constructor(private http: HttpClient) { }

  //#region API
  public createUploadSas(data: CreateUploadRequestDTO): Observable<CreateUploadResponseDTO> {
    return this.http.post<CreateUploadResponseDTO>(this.pathAPI + this.controller + 'CreateUploadSas', data);
  }
  //#endregion

  //#region Functions  
 /** Código de error cuando la subida se aborta (pausa/cancelación por el usuario). */
  static readonly ABORTED_ERROR = 'UPLOAD_ABORTED';

  async uploadLargeFileResumable(
    uploadUrl: string,
    blobname: string,
    file: File,
    blockSize: number,
    concurrency: number,
    renewSas: () => Promise<string>,              // callback para renovar SAS si expira
    onProgress: (p: number) => void,
    abortSignal?: AbortSignal
  ) {
    let client = new BlockBlobClient(uploadUrl);

    const totalBlocks = Math.ceil(file.size / blockSize);

    // Carga checkpoint
    const existing = this.loadCheckpoint(blobname);
    const cp: UploadCheckpoint = existing ?? {
      blobname,
      fileName: file.name,
      fileSize: file.size,
      blockSize,
      uploaded: [],
      createdAt: Date.now()
    };

    // Validación: si cambió el archivo, no reanudes con el checkpoint viejo
    if (cp.fileName !== file.name || cp.fileSize !== file.size || cp.blockSize !== blockSize) {
      // Reset seguro
      cp.fileName = file.name;
      cp.fileSize = file.size;
      cp.blockSize = blockSize;
      cp.uploaded = [];
      cp.createdAt = Date.now();
      this.saveCheckpoint(cp);
    }

    const uploadedSet = new Set<number>(cp.uploaded);

    const pendingIndexes: number[] = [];
    for (let i = 0; i < totalBlocks; i++) {
      if (!uploadedSet.has(i)) pendingIndexes.push(i);
    }

    const reportProgress = () => {
      const uploadedBytes = Math.min(uploadedSet.size * blockSize, file.size);
      const percent = Math.round((uploadedBytes / file.size) * 100);
      onProgress(percent);
    };

    reportProgress();

    // Worker para subir 1 bloque
    const stageOne = async (i: number) => {
      if (abortSignal?.aborted) {
        const err = new Error(FileService.ABORTED_ERROR);
        (err as any).code = FileService.ABORTED_ERROR;
        throw err;
      }
      const start = i * blockSize;
      const end = Math.min(start + blockSize, file.size);
      const chunk = file.slice(start, end);
      const blockId = this.blockIdFromIndex(i);
      const options = abortSignal ? { abortSignal } : undefined;

      try {
        await client.stageBlock(blockId, chunk, chunk.size, options);
      } catch (e: any) {
        if (e?.name === 'AbortError' || e?.message === FileService.ABORTED_ERROR) {
          const err = new Error(FileService.ABORTED_ERROR);
          (err as any).code = FileService.ABORTED_ERROR;
          throw err;
        }
        // si el SAS expiró o auth falla, renueva y reintenta UNA vez
        const msg = String(e?.message ?? '');
        if (msg.includes('403') || msg.toLowerCase().includes('authentication')) {
          const newUrl = await renewSas();
          client = new BlockBlobClient(newUrl);
          await client.stageBlock(blockId, chunk, chunk.size, options);
        } else {
          throw e;
        }
      }

      uploadedSet.add(i);
      cp.uploaded = Array.from(uploadedSet);
      this.saveCheckpoint(cp);
      reportProgress();
    };

    // Subir pendientes con concurrencia
    await this.mapLimit(pendingIndexes, concurrency, stageOne);

    if (abortSignal?.aborted) {
      const err = new Error(FileService.ABORTED_ERROR);
      (err as any).code = FileService.ABORTED_ERROR;
      throw err;
    }

    // Commit final (lista completa en orden)
    const allBlockIds = Array.from({ length: totalBlocks }, (_, i) => this.blockIdFromIndex(i));
    await client.commitBlockList(allBlockIds, abortSignal ? { abortSignal } : undefined);

    onProgress(100);

    // Limpia checkpoint al completar
    this.clearCheckpoint(blobname);

    return { blockIds: allBlockIds };
  }

  blockIdFromIndex(i: number): string {
    // Azure requiere Base64. Usamos índice padded para que sea estable.
    const padded = i.toString().padStart(8, '0'); // "00000001"
    return btoa(padded);
  }

  ckKey(blobname: string) {
    return `upload_ck:${blobname}`;
  }

  async mapLimit<T>(
    items: T[],
    concurrency: number,
    worker: (item: T) => Promise<void>
  ) {
    let idx = 0;
    const runners = Array.from({ length: Math.max(1, concurrency) }, async () => {
      while (idx < items.length) {
        const current = idx++;
        await worker(items[current]);
      }
    });
    await Promise.all(runners);
  }

  //#endregion


  //#region Local Storage

  loadCheckpoint(blobname: string): UploadCheckpoint | null {
    const raw = localStorage.getItem(this.ckKey(blobname));
    return raw ? JSON.parse(raw) : null;
  }

  saveCheckpoint(cp: UploadCheckpoint) {
    localStorage.setItem(this.ckKey(cp.blobname), JSON.stringify(cp));
  }
  
  clearCheckpoint(blobname: string) {
    localStorage.removeItem(this.ckKey(blobname));
  }

  //#endregion

}
